import { html, css } from 'lit';
import { StateView } from '../../views/state-view.js';
import { DocumentService, SUPPORTED_FILE_EXTENSIONS, TABULAR_FILE_EXTENSIONS } from '../../services/knowledge/document-service.js';
import { notify } from '../../utils/notify.js';

/**
 * Global drag and drop file upload component
 * Handles file uploads anywhere in the application with a full-screen overlay
 */
export class GlobalDragDrop extends StateView {
  static properties = {
    isDraggingOver: { type: Boolean },
    isProcessing: { type: Boolean },
    uploadProgress: { type: Object },
    showProgress: { type: Boolean },
    notifications: { type: Array },
    isDisabled: { type: Boolean },
    dragCounter: { type: Number },
    useGemini: { type: Boolean } // 🚀 NEW: Enable Gemini 2.0 Flash processing
  };

  constructor() {
    super();
    this.isDraggingOver = false;
    this.isProcessing = false;
    this.uploadProgress = new Map();
    this.showProgress = false;
    this.notifications = [];
    this._dragLeaveTimeout = null;
    this.isDisabled = false;
    this.dragCounter = 0;
    this.useGemini = false; // 🚀 Set to true to enable Gemini 2.0 Flash by default
    
    // Initialize DocumentService with error handling
    try {
      this.documentService = new DocumentService();
      console.log('[GlobalDragDrop] DocumentService initialized successfully');
    } catch (error) {
      console.error('[GlobalDragDrop] Failed to initialize DocumentService:', error);
      this.isDisabled = true;
    }

    // Bind event handlers
    this.handleDragEnter = this.handleDragEnter.bind(this);
    this.handleDragLeave = this.handleDragLeave.bind(this);
    this.handleDragOver = this.handleDragOver.bind(this);
    this.handleDrop = this.handleDrop.bind(this);
    this._resetDragState = this._resetDragState.bind(this);
    
    console.log('[GlobalDragDrop] Constructor completed');
  }

  connectedCallback() {
    super.connectedCallback();
    console.log('[GlobalDragDrop] Connected - attaching global drag handlers');
    
    // Attach global drag and drop listeners
    // Use capture phase to ensure we receive events even if inner components stop propagation
    window.addEventListener('dragenter', this.handleDragEnter, true);
    window.addEventListener('dragleave', this.handleDragLeave, true);
    window.addEventListener('dragover', this.handleDragOver, true);
    window.addEventListener('drop', this.handleDrop, true);
    // Some browsers do not reliably bubble 'drop'; also listen on document and 'dragend'
    document.addEventListener('drop', this.handleDrop, true);
    window.addEventListener('dragend', this._resetDragState, true);
    window.addEventListener('blur', this._resetDragState);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    console.log('[GlobalDragDrop] Disconnected - removing global drag handlers');
    
    // Remove global drag and drop listeners
    window.removeEventListener('dragenter', this.handleDragEnter, true);
    window.removeEventListener('dragleave', this.handleDragLeave, true);
    window.removeEventListener('dragover', this.handleDragOver, true);
    window.removeEventListener('drop', this.handleDrop, true);
    document.removeEventListener('drop', this.handleDrop, true);
    window.removeEventListener('dragend', this._resetDragState, true);
    window.removeEventListener('blur', this._resetDragState);
    
    // Clear any pending timeouts and reset state
    if (this._dragLeaveTimeout) {
      clearTimeout(this._dragLeaveTimeout);
    }
    
    // Reset drag state
    this._resetDragState();
  }

  handleDragEnter(event) {
    // Only handle file drags
    if (!this._isFileDrag(event)) {
      return;
    }
    
    // Don't intercept if dragging over context tray or its components
    if (this._isDragOverContextTray(event)) {
      return;
    }
    
    event.preventDefault();
    event.stopPropagation();
    
    // Increment drag counter
    this.dragCounter++;
    
    // Clear any pending timeout (legacy cleanup)
    if (this._dragLeaveTimeout) {
      clearTimeout(this._dragLeaveTimeout);
      this._dragLeaveTimeout = null;
    }
    
    // Show overlay on first enter
    if (!this.isDisabled && !this.isDraggingOver && this.dragCounter === 1) {
      this.isDraggingOver = true;
      this.requestUpdate();
    }
  }

  handleDragOver(event) {
    // Only handle file drags
    if (!this._isFileDrag(event)) {
      return;
    }
    
    // Don't intercept if dragging over context tray or its components
    if (this._isDragOverContextTray(event)) {
      return;
    }
    
    event.preventDefault();
    event.stopPropagation();
  }

  handleDragLeave(event) {
    // Only handle file drags
    if (!this._isFileDrag(event)) {
      return;
    }
    
    // Don't intercept if dragging over context tray or its components
    if (this._isDragOverContextTray(event)) {
      return;
    }
    
    event.preventDefault();
    event.stopPropagation();
    
    // Decrement drag counter
    this.dragCounter--;
    
    // Hide overlay when no more drag contexts
    if (this.dragCounter <= 0) {
      this.isDraggingOver = false;
      this.dragCounter = 0; // Ensure it doesn't go negative
      this.requestUpdate();
    }
  }

  async handleDrop(event) {
    // Only handle file drags
    if (!this._isFileDrag(event)) {
      return;
    }
    
    // Don't intercept if dropping on context tray or its components
    if (this._isDragOverContextTray(event)) {
      console.log('[GlobalDragDrop] Drop on context-tray/resource-upload - letting component handle it');
      this._resetDragState(); // Still reset our state
      return;
    }
    
    event.preventDefault();
    event.stopPropagation();
    
    // Always reset drag state on drop
    this._resetDragState();
    
    // Clear any pending timeout (legacy cleanup)
    if (this._dragLeaveTimeout) {
      clearTimeout(this._dragLeaveTimeout);
      this._dragLeaveTimeout = null;
    }
    
    const files = event.dataTransfer.files;
    if (files && files.length > 0 && !this.isDisabled) {
      await this._processFiles(files);
    }
    
    this.requestUpdate();
  }

  _resetDragState() {
    // Clear any pending timeout (legacy cleanup)
    if (this._dragLeaveTimeout) {
      clearTimeout(this._dragLeaveTimeout);
      this._dragLeaveTimeout = null;
    }
    this.isDraggingOver = false;
    this.dragCounter = 0;
  }

  _isFileDrag(event) {
    if (!event.dataTransfer) return false;
    
    // Check if the drag contains files
    return event.dataTransfer.types && 
           (event.dataTransfer.types.includes('Files') || 
            event.dataTransfer.types.includes('application/x-moz-file'));
  }

  _isDragOverContextTray(event) {
    // Check if we're dragging over the context tray or resource-upload component
    // These components should handle their own drag events
    const target = event.target;
    if (!target) return false;
    
    // Check if target or any parent is context-tray or resource-upload
    let element = target;
    while (element) {
      const tagName = element.tagName?.toLowerCase();
      if (tagName === 'context-tray' || 
          tagName === 'resource-upload' ||
          element.hasAttribute?.('data-global') === false) {
        return true;
      }
      // Also check for the resource upload label/area by class
      if (element.classList?.contains('resource-upload-area')) {
        return true;
      }
      element = element.parentElement;
    }
    return false;
  }

  async _processFiles(files) {
    if (this.isProcessing) return;
    
    // Check if service is available and user is authenticated
    if (this.isDisabled) {
      this._showNotification('File upload service is not available', 'error');
      return;
    }
    
    if (!this.user?.id) {
      this._showNotification('Please log in to upload files', 'error');
      return;
    }
    
    this.isProcessing = true;
    this.showProgress = true;
    this.requestUpdate();
    
    try {
      // Process files sequentially to avoid overwhelming the server
      for (let i = 0; i < files.length; i++) {
        const file = files[i];
        await this._processFile(file);
      }
    } catch (error) {
      console.error('[GlobalDragDrop] Error processing files:', error);
      this._showNotification(`Error processing files: ${error.message}`, 'error');
    } finally {
      this.isProcessing = false;
      this.showProgress = false;
      this.uploadProgress.clear();
      this.requestUpdate();
    }
  }

  async _processFile(file) {
    try {
      // Check file type
      const extension = '.' + file.name.split('.').pop().toLowerCase();
      if (!(extension in SUPPORTED_FILE_EXTENSIONS)) {
        this._showNotification(`Unsupported file type: ${file.name}`, 'error');
        return;
      }
      
      // Update progress
      this._updateFileProgress(file.name, 10, 'uploading');
      
      // Prepare metadata
      const metadata = {
        title: file.name,
        description: `Uploaded via drag and drop on ${new Date().toLocaleString()}`
      };
      
      // 🚀 NEW: Support Gemini 2.0 Flash processing (20x cost savings!)
      const processingOptions = {
        extractLayout: true
      };
      
      // Enable Gemini 2.0 Flash if configured (20x cost savings!)
      if (this.useGemini) {
        processingOptions.parseMode = 'gemini_flash';
        console.log('[GlobalDragDrop] Using Gemini 2.0 Flash for document processing (95% cost savings!)');
      }
      
      // Upload file
      let document;
      if (extension in TABULAR_FILE_EXTENSIONS) {
        document = await this.documentService.uploadTabular(file, metadata, processingOptions);
      } else {
        document = await this.documentService.uploadDocument(file, metadata, processingOptions);
      }
      
      // Update progress
      this._updateFileProgress(file.name, 100, 'complete');
      
      // Show success notification with processing method info
      const processingMethod = this.useGemini ? ' (Gemini 2.0 Flash)' : '';
      this._showNotification(`✓ ${file.name}${processingMethod}`, 'success');
      
      // Emit custom event for other components
      this._emitFileUploaded(file, document);
      
    } catch (error) {
      console.error('[GlobalDragDrop] Error uploading file:', error);
      this._updateFileProgress(file.name, 0, 'error');
      this._showNotification(`Failed to upload ${file.name}: ${error.message}`, 'error');
    }
  }

  _updateFileProgress(fileName, progress, status) {
    this.uploadProgress.set(fileName, { progress, status });
    // Only update if we're showing progress to avoid unnecessary renders
    if (this.showProgress) {
      this.requestUpdate();
    }
  }

  _showNotification(message, type = 'info', duration = 5000) { notify(message, type, duration); }

  _emitFileUploaded(file, document) {
    const event = new CustomEvent('file-uploaded', {
      detail: { file, document },
      bubbles: true,
      composed: true
    });
    this.dispatchEvent(event);
  }

  render() {
    // Remove debug logging to reduce console spam
    return html`
      ${this.isDraggingOver ? this._renderDragOverlay() : ''}
      ${this.showProgress ? this._renderProgressPanel() : ''}
    `;
  }

  _renderDragOverlay() {
    return html`
      <div class="fixed inset-0 bg-blue-500 bg-opacity-75 flex items-center justify-center z-50 pointer-events-none">
        <div class="text-white text-2xl font-bold text-center p-4">
          <svg xmlns="http://www.w3.org/2000/svg" class="h-16 w-16 mx-auto mb-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
          </svg>
          Drop files anywhere to upload!
        </div>
      </div>
    `;
  }

  _renderProgressPanel() {
    return html`
      <div class="fixed top-4 right-4 bg-white rounded-lg shadow-lg p-4 max-w-sm z-40 max-h-96 overflow-y-auto">
        <h3 class="text-lg font-medium mb-2">Uploading Files</h3>
        ${Array.from(this.uploadProgress.entries()).map(([fileName, progress]) => html`
          <div class="mb-2">
            <div class="text-sm font-medium">${fileName}</div>
            <div class="w-full bg-gray-200 rounded-full h-2">
              <div class="bg-blue-500 h-2 rounded-full transition-all duration-300" 
                   style="width: ${progress.progress}%"></div>
            </div>
            <div class="text-xs text-gray-500 mt-1">${progress.status}</div>
          </div>
        `)}
      </div>
    `;
  }

  _renderNotifications() { return html``; }

  _formatFileSize(bytes) {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  }

  static styles = css`
    :host {
      position: fixed;
      top: 0;
      left: 0;
      width: 100%;
      height: 100%;
      pointer-events: none;
      z-index: 1000;
    }
  `;
}

customElements.define('global-drag-drop', GlobalDragDrop); 